import React from 'react';

import moment from 'moment-tz';
import {Link} from 'react-router';

import {TASKS_ALL_PAGE} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {UserSerializedType} from 'sweet-types';

export interface BadgeStatusType {
  badgeType:
    | 'tfa'
    | 'sso_disabled'
    | 'sso_enabled'
    | 'invitation_pending'
    | 'guess_access_expired'
    | 'guest_expiration_date'
    | 'invalid_email'
    | 'no_tasks'
    | 'no_account_access';
  color: string;
  icon: string;
  badgeDescription: React.ReactNode;
  alertDescription: React.ReactNode;
  showOnCard: boolean;
}

export const getUserStatusBadges = (
  user: UserSerializedType,
  isSaml: boolean
): Array<BadgeStatusType> => {
  let statuses: Array<BadgeStatusType> = [];

  if (user.is_two_factor) {
    statuses.push({
      badgeType: 'tfa',
      color: 'success',
      icon: 'fa fa-check',
      badgeDescription: gt('2FA'),
      alertDescription: gt(
        'Two Factor Authentication (2FA) is enabled for this member'
      ),
      showOnCard: false,
    });
  }

  if (user.restrict_saml_only_login) {
    statuses.push({
      badgeType: 'sso_enabled',
      color: 'success',
      icon: 'far fa-sign-in',
      badgeDescription: gt('SSO enabled'),
      alertDescription: gt('SSO enabled'),
      showOnCard: false,
    });
  }

  if (!user.restrict_saml_only_login && isSaml) {
    statuses.push({
      badgeType: 'sso_disabled',
      color: 'warning',
      icon: 'far fa-sign-in',
      badgeDescription: gt('SSO disabled'),
      alertDescription: gt('SSO disabled'),
      showOnCard: false,
    });
  }

  if (!user.is_active && user.is_guest_until == null) {
    statuses.push({
      badgeType: 'invitation_pending',
      color: 'primary',
      icon: 'far fa-envelope',
      badgeDescription: gt('Pending invitation'),
      alertDescription: gt(
        "An invitation was sent but it wasn't accepted yet."
      ),
      showOnCard: true,
    });
  }

  if (user.is_guest_until != null) {
    const guestDescription = gt('Guest member with limited account access');
    const untilMoment = moment(user.is_guest_until);

    const tasksLink = (
      <div className="mt-2">
        <Link className="alert-link" to={`${TASKS_ALL_PAGE}?user=${user.url}`}>
          {gt('See tasks assigned to this guest')}
        </Link>
      </div>
    );

    if (moment().isAfter(user.is_guest_until)) {
      statuses.push({
        badgeType: 'guess_access_expired',
        color: 'danger',
        icon: 'fas fa-user-clock',
        badgeDescription: `${gt('Guest, expired %s', untilMoment.fromNow())}`,
        alertDescription: (
          <div>
            <div>{guestDescription}.</div>
            <div>{gt('Access expired on %s', untilMoment.format('lll'))}</div>
            {tasksLink}
          </div>
        ),
        showOnCard: false,
      });
    } else {
      statuses.push({
        badgeType: 'guest_expiration_date',
        color: 'primary',
        icon: 'fas fa-user-clock',
        badgeDescription: `${gt(
          'Guest, access expires %s',
          untilMoment.fromNow()
        )}`,
        alertDescription: (
          <div>
            <div>{guestDescription}.</div>
            <div>{gt('Access expires on %s', untilMoment.format('lll'))}</div>
            {tasksLink}
          </div>
        ),
        showOnCard: false,
      });
    }
  }

  if (user.emailstatus === 'rejected' || user.emailstatus === 'bounced') {
    statuses.push({
      badgeType: 'invalid_email',
      color: 'danger',
      icon: 'far fa-envelope',
      badgeDescription: gt('Invalid email'),
      alertDescription: gt(
        'We are unable to send email to this email address.'
      ),
      showOnCard: true,
    });
  }

  if (user.is_account_only) {
    if (user.is_guest_until != null) {
      statuses.push({
        badgeType: 'no_tasks',
        color: 'warning',
        icon: 'fa fa-exclamation-triangle',
        badgeDescription: gt('No assigned tasks'),
        alertDescription: gt(
          "This guest hasn't been assigned to any task yet."
        ),
        showOnCard: true,
      });
    } else {
      statuses.push({
        badgeType: 'no_account_access',
        color: 'warning',
        icon: 'fa fa-exclamation-triangle',
        badgeDescription: gt('No Account Access'),
        alertDescription: gt(
          "This member hasn't been given permission to see anything on your account yet. Why not add them to a team?"
        ),
        showOnCard: true,
      });
    }
  }

  return statuses;
};
